import {
  ActivitySelectors,
  ContactSelectors,
  Event,
  ParentState,
  TravelerSelectors,
  useCheckoutSend,
  useCheckoutStateSelector,
} from "@checkout/index";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useActivitiesTravelerValidation } from "../components/ActivitiesTravelerSelection";
import { ActivitiesCheckoutState } from "../stateMachine";
import { useMobileCheckoutPage } from "@/modules/checkout/components/MobileCheckoutPage/hook";
import type {
  ButtonConfig,
  StepConfig,
  UseFunnelMobileCheckoutReturn,
} from "@/modules/checkout/components/MobileCheckoutPage/types";

const stepToStateMap = {
  [ParentState.contactAndTravelerInformation]: 1,
  [ActivitiesCheckoutState.primaryParticipantSelection]: 2,
  [ActivitiesCheckoutState.activityInformation]: 3,
  [ParentState.cardPayment]: 4,
  [ParentState.review]: 5,
};

export const useActivitiesMobileCheckoutPage =
  (): UseFunnelMobileCheckoutReturn => {
    const send = useCheckoutSend();
    const { t } = useI18nContext();

    const {
      stepMap: defaultStepMap,
      hideDockedCta,
      step,
      hidePriceBreakdown,
    } = useMobileCheckoutPage({
      stepToStateMap,
    });

    const isTravellerSelected = useCheckoutStateSelector(
      TravelerSelectors.getIsATravelerSelected
    );
    const isContactInfoComplete = useCheckoutStateSelector(
      ContactSelectors.getContactInformationValidated
    );
    const { isValid: activitiesIsValid } = useActivitiesTravelerValidation();
    const canContinuePassengerInformation =
      isTravellerSelected && isContactInfoComplete && activitiesIsValid;

    const primaryParticipantId = useCheckoutStateSelector(
      (state) =>
        state.context[ParentState.activityInformation]?.primaryParticipantId
    );
    const canContinuePrimaryParticipant = !!primaryParticipantId;

    const activityInformationValidated = useCheckoutStateSelector(
      ActivitySelectors.getActivityInformationValidated
    );

    const stepMap = {
      1: {
        ...defaultStepMap[1],
        disabled: !canContinuePassengerInformation,
      },
      2: {
        buttonLabel: t("continue"),
        disabled: !canContinuePrimaryParticipant,
      },
      3: {
        buttonLabel: t("activities.checkout.additionalDetails.continue"),
        disabled: !activityInformationValidated,
      },
      4: { ...defaultStepMap[2] },
      5: { ...defaultStepMap[3] },
    };

    const stepInfo: StepConfig = stepMap[step] ?? {
      buttonLabel: t("continue"),
      disabled: false,
    };

    // Build buttons array
    const buttons: ButtonConfig[] = [
      {
        label: stepInfo.buttonLabel,
        onClick: () => send(Event.NEXT),
        disabled: stepInfo.disabled,
      },
    ];

    return {
      step,
      hideDockedCta,
      buttons,
      hidePriceBreakdown,
    };
  };
